/**
 * Training auf dem Bereichs-Board Sport (F-34 E2, F-01): die heutige Einheit
 * und der Wochenfortschritt, mit Link zur Trainingsseite. Erscheint nur im
 * gewaehlten Sport-Bereich.
 */
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Dumbbell } from 'lucide-react';

import type { ID } from '@/domain/types';
import { today } from '@/lib/datetime';
import { zahl1 } from '@/modules/training/format';
import { useTrainingWoche } from '@/modules/training/useTrainingWoche';

export function BereichTraining({ areaId, immer }: { areaId: ID; /** Auf der Seite von "Gesundheit & Körper" immer zeigen. */ immer?: boolean }) {
  const heute = today();
  const w = useTrainingWoche(heute);
  const d = w && (immer || w.einstellungen?.sportBereichId === areaId)
    ? { heute: (w.tage.find((t) => t.datum === heute)?.einheiten ?? []), woche: { geplant: w.geplant, erledigt: w.erledigt, ausgefallen: w.ausgefallen }, umfang: w.umfang }
    : null;
  if (!d) return null;
  const anteil = d.woche.geplant ? d.woche.erledigt / d.woche.geplant : 0;

  return (
    <section aria-label="Training" className="flex flex-col gap-2 rounded-lg border border-line bg-surface px-4 py-3.5">
      <div className="flex items-center gap-2">
        <h2 className="flex-1 text-sm font-semibold">Training</h2>
        <Link to="/training" className="text-[12px] text-accent-text hover:underline">Zur Trainingsseite</Link>
      </div>
      {d.heute.length === 0 ? <p className="text-sm text-ink-muted">Heute steht keine Einheit im Plan.</p> : (
        <ul className="flex flex-col gap-1">
          {d.heute.map((g) => (
            <li key={g.einheit.id} className="flex items-center gap-2 text-sm">
              {g.erledigt ? <CheckCircle2 className="size-4 text-ok" aria-label="absolviert" /> : <Circle className="size-4 text-ink-faint" aria-label="offen" />}
              <Link to="/training" className="min-w-0 flex-1 truncate hover:underline">{g.einheit.name}</Link>
              {g.einheit.uhrzeit && <span className="tabular-nums text-ink-muted">{g.einheit.uhrzeit}</span>}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline justify-between text-[12px]">
          <span className="text-ink-muted">Diese Woche</span>
          <span className="tabular-nums">{d.woche.erledigt} von {d.woche.geplant} {d.woche.geplant === 1 ? 'Einheit' : 'Einheiten'}{d.woche.ausgefallen ? ` · ${d.woche.ausgefallen} ausgefallen` : ''}</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-line" role="progressbar" aria-label="Wochenfortschritt Training" aria-valuemin={0} aria-valuemax={d.woche.geplant} aria-valuenow={d.woche.erledigt}>
          <div className="h-full rounded-full bg-accent" style={{ width: `${Math.round(anteil * 100)}%` }} />
        </div>
        <p className="flex items-center gap-1 text-[12px] text-ink-muted"><Dumbbell className="size-3.5" aria-hidden /> {d.umfang.einheiten} {d.umfang.einheiten === 1 ? 'Einheit' : 'Einheiten'}, {Math.round(d.umfang.minuten)} min{d.umfang.km ? `, ${zahl1(d.umfang.km)} km` : ''}</p>
      </div>
    </section>
  );
}
